import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Spinner, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function LeadMagnetTopic() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('input'); // input, generating, selecting
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form data
  const [preferredFormat, setPreferredFormat] = useState('PDF Guide');
  const [topicsResponse, setTopicsResponse] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [savedTopic, setSavedTopic] = useState(null);
  const [targetMarket, setTargetMarket] = useState('');

  const formatOptions = [
    'PDF Guide',
    'Checklist',
    'Template',
    'Video Tutorial',
    'Webinar Recording',
    'Tool/Calculator',
    'Quiz/Assessment'
  ];

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Get target market for personalized title
    try {
      const marketResponse = await fetch('/api/target-market/primary', {
        credentials: 'include'
      });

      if (marketResponse.ok) {
        const marketData = await marketResponse.json();
        if (marketData.primary_niche) {
          setTargetMarket(marketData.primary_niche.niche_name || '');
        }
      }
    } catch (err) {
      console.error('Error loading target market:', err);
    }

    // Check if user has saved topic
    try {
      const response = await fetch('/api/assets/lead-magnet/get-topic', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.topic) {
          setSavedTopic(data.topic);
          setSelectedTopic(data.topic.topic);
          setPreferredFormat(data.topic.format);
          setStage('selecting');
        }
      }
    } catch (err) {
      console.error('Error loading saved topic:', err);
    }

    setLoading(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');

    try {
      const response = await fetch('/api/assets/lead-magnet/generate-topics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferred_format: preferredFormat })
      });

      if (response.ok) {
        const data = await response.json();
        setTopicsResponse(data.topics);
        setStage('selecting');
      } else {
        const errorData = await response.json();
        setError(`Failed to generate topics: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error generating topics: ' + err.message);
    }

    setGenerating(false);
  };

  const handleSave = async () => {
    if (!selectedTopic.trim()) {
      setError('Please enter your selected topic');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/assets/lead-magnet/save-topic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          topic: selectedTopic,
          preferred_format: preferredFormat
        })
      });

      if (response.ok) {
        // Navigate to content creation page
        navigate('/lead-magnet-content');
      } else {
        const errorData = await response.json();
        setError(`Failed to save topic: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving topic: ' + err.message);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="assets-creation" />
      <Row className="mb-4">
        <Col>
          <h1>Lead Magnet Topic Selection{targetMarket && ` for ${targetMarket}`}</h1>
          <p className="text-muted">
            Choose a compelling topic for your lead magnet that attracts your ideal customers.
          </p>
        </Col>
      </Row>

      {/* Command Center Widget Upgrade Notice */}
      <Row className="mb-4">
        <Col>
          <Alert variant="info" className="border-0" style={{
            background: 'linear-gradient(135deg, #e8f4fd 0%, #f0e6fa 100%)',
            borderLeft: '4px solid #6f42c1'
          }}>
            <div className="d-flex align-items-start">
              <span style={{ fontSize: '1.5rem', marginRight: '12px' }}>✨</span>
              <div>
                <strong style={{ color: '#6f42c1' }}>Be on the Bleeding Edge!</strong>
                <p className="mb-1 mt-1">
                  When you upgrade to the <strong>Command Center</strong>, we can build you a <strong>customized interactive widget</strong> for
                  your website instead of a downloadable PDF. It will be tailored specifically to your business, your products,
                  and your target market — a truly unique lead magnet that stands out!
                </p>
                <p className="mb-0 text-muted small">
                  Of course, you can still build a great PDF lead magnet right here if you prefer.
                </p>
              </div>
            </div>
          </Alert>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Input Stage */}
      {stage === 'input' && (
        <>
          <Row className="mb-4">
            <Col md={8}>
              <Card>
                <Card.Body>
                  <h4 className="mb-4">🎯 What Format Works Best?</h4>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Preferred Lead Magnet Format:</strong></Form.Label>
                    <Form.Select
                      value={preferredFormat}
                      onChange={(e) => setPreferredFormat(e.target.value)}
                      size="lg"
                    >
                      {formatOptions.map(format => (
                        <option key={format} value={format}>{format}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Don't worry - you can create multiple lead magnets with different formats later.
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Generating Topic Ideas...
                        </>
                      ) : (
                        'Generate Lead Magnet Topics →'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="bg-light">
                <Card.Body>
                  <h5 className="mb-3">💡 What Makes a Great Lead Magnet?</h5>
                  <ul className="small mb-0">
                    <li>Solves a specific, urgent problem</li>
                    <li>Delivers a quick win</li>
                    <li>Requires minimal time investment</li>
                    <li>Naturally leads to your core product</li>
                    <li>Easy to consume and implement</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Selecting Stage */}
      {stage === 'selecting' && (
        <>
          {savedTopic && (
            <Row className="mb-4">
              <Col>
                <Alert variant="success">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>✓ You have a saved lead magnet:</strong> {savedTopic.topic}
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => navigate('/lead-magnet-content')}
                      >
                        View Content
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => navigate('/lead-magnet-landing')}
                      >
                        View Landing Page
                      </Button>
                    </div>
                  </div>
                </Alert>
              </Col>
            </Row>
          )}

          <Row className="mb-4">
            <Col md={8}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h4 className="mb-1">📋 Generated Topic Ideas</h4>
                      <Badge bg="secondary">{preferredFormat}</Badge>
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setStage('input');
                        setTopicsResponse('');
                        setSelectedTopic('');
                      }}
                    >
                      ← Change Format
                    </Button>
                  </div>

                  {topicsResponse && (
                    <Alert variant="info" className="mb-4">
                      <strong>Review the AI-generated topics below.</strong> You can select one or use them as inspiration to write your own.
                    </Alert>
                  )}

                  <div className="mb-4" style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '8px',
                    lineHeight: '1.6',
                    maxHeight: '500px',
                    overflowY: 'auto'
                  }}>
                    {topicsResponse || 'Loading previous selection...'}
                  </div>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Your Selected/Customized Topic:</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={selectedTopic}
                      onChange={(e) => setSelectedTopic(e.target.value)}
                      placeholder="Copy and paste your chosen topic, or write your own..."
                    />
                    <Form.Text className="text-muted">
                      You can customize any of the AI suggestions or write your own topic.
                    </Form.Text>
                  </Form.Group>

                  <div className="text-center mb-3">
                    <Button
                      variant="link"
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Regenerating...
                        </>
                      ) : (
                        '🔄 Regenerate Different Topics'
                      )}
                    </Button>
                  </div>

                  <div className="d-flex justify-content-between">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/dashboard/marketing-strategy')}
                    >
                      Save for Later
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleSave}
                      disabled={saving || !selectedTopic.trim()}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save & Continue →'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="bg-light mb-3">
                <Card.Body>
                  <h5 className="mb-3">✏️ Customization Tips</h5>
                  <ul className="small mb-0">
                    <li>Make it benefit-focused (not feature-focused)</li>
                    <li>Use specific numbers when possible</li>
                    <li>Address a painful problem directly</li>
                    <li>Make the outcome clear</li>
                    <li>Keep it concise and memorable</li>
                  </ul>
                </Card.Body>
              </Card>

              {savedTopic && (
                <Alert variant="success">
                  <strong>✓ Previously Saved</strong><br />
                  You can update your topic and save again, or continue to the next step.
                </Alert>
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

export default LeadMagnetTopic;
